import { Injectable } from '@angular/core';
import { EventName, Events } from '../utils/events';
import { API } from './api';
import { Class, ClassProvider } from './class';
import { BaseProvider } from './base';
import { LocalStorageProvider } from './local-storage';
import { BoostJobUXState, BoostProvider } from './boost';
import { BrainVersionType } from './brain';
import { UserProvider } from './user';
import { AdherenceToNormal, TruePositiveDefinition } from '../providers/evaluate_brain/anomaly-stats.service';
import { TranslateService } from '@ngx-translate/core';
import { ROICoordinates } from './workspace/canvas';

export class DatasetImageCount {
    total = 0;
    tagged = 0;
}

export interface DatasetUserTaggedCount {
    userIdentifier: string;
    email: string;
    count: number;
    name: string;
}

export class DatasetComments {
    total = 0;
}

export interface UserAssignmentStats {
    inProgress: number;
    completed: number;
    classes: number;
    tagged: number;
    untagged: number;
    images: number;
    numFrozenBrains: number;
    numBrainDownloads: number;
}

export interface DatasetBrain {
    classCounts?: any;
    id: string;
    accuracy: number;
    numValidationPolygons: number;
    numTrainedPolygons: number;
    numFrames: number;
    dateTrained: Date;
    dateFrozen: Date | null;
    dateHiFiCreated: Date | null;
    dirty: boolean;
    hiFiDirty: boolean;
    frozenDirty: boolean;
    promoted: boolean;
    numDownloads: number;
    averagePrecision: number;
    averageRecall: number;
    miapCOCO: number;
    miapVOC: number;
}

export interface DatasetDuplicationOptions {
    dataset: Dataset;
    duplicatedDatasetUsageType: DatasetType;
    duplicatedDatasetName: string;
    includeUntaggedAssets: boolean;
    classIdentifiers?: string[];
    duplicateAnotherOnSuccess: boolean;
    usedDefaultName: boolean;
}

export interface DatasetROIs {
    roiIdentifier: string;
    data: number[][];
}

export enum DatasetType {
    Segmentation = 'Segmentation',
    ObjectDetection = 'Detection',
    SceneRecognition = 'Scene Recognition',
    AnomalyRecognition = 'Anomaly Recognition'
}

export interface ClassesChartData {
    color?: string;
    name?: string;
    trainingValue: number;
    validationValue?: number;
}

export interface ClassIdentifierPolygonCountsMap {
    [key: string]: number;
}

export interface PurposeCounts {
    train: number | null;
    validation: number | null;
}

export interface ClassIdentifierPurposeCountsMap {
    [key: string]: PurposeCounts;
}

export const ANOMALY_RECOGNITION_CLASS_NAME_ANOMALY = 'Anomaly';

export enum DatasetChecks {
    AssetsIdenticiallySized = 'assets-identically-sized'
}

export class Dataset {
    id: string;
    type: DatasetType;
    name: string;
    imageCounts: DatasetImageCount = new DatasetImageCount();
    classCounts: ClassIdentifierPolygonCountsMap;
    classPurposeCounts: ClassIdentifierPurposeCountsMap;
    numPolygons: number;
    previewImageUrls: string[];
    projectId: string;
    users: any[] = [];
    commentCounts: DatasetComments = new DatasetComments();
    approved = false;
    perUserTaggedCounts: DatasetUserTaggedCount[];
    truePositivePreference: TruePositiveDefinition;
    brain: DatasetBrain | undefined;
    adherenceToNormal: AdherenceToNormal;
    hifiAdherenceToNormal: AdherenceToNormal;
    roi: ROICoordinates | null;
    enableStaticClassifier: boolean;
    enableAnomalyHiFi: boolean;
    userDefinedValidationSet: boolean;
    learnAsYouGo?: boolean;
    learningTrainFrames: number | null;
    learningValidationFrames: number | null;

    getClassPercentage(count): number {
        if (!this.numPolygons || !count) {
            return 0;
        } else {
            return Math.round((count / this.numPolygons) * 100);
        }
    }

    hasPolygons(): boolean {
        return (
            this.classCounts && Object.keys(this.classCounts).findIndex(classId => this.classCounts[classId] > 0) > -1
        );
    }

    isChecked() {}

    public canOptimizeDataset(): boolean {
        return (
            this.classCounts &&
            Object.keys(this.classCounts).length > 0 &&
            this.hasValidationImages() &&
            this.hasTrainImages()
        );
    }

    public hasValidationImages(): boolean {
        return this.brain !== undefined && this.brain.numValidationPolygons > 0;
    }

    public hasTrainImages(): boolean {
        return this.brain !== undefined && this.brain.numTrainedPolygons > 0;
    }

    public isAnomalyRecognition(): boolean {
        return this.type === DatasetType.AnomalyRecognition;
    }

    public isObjectDetection(): boolean {
        return this.type === DatasetType.ObjectDetection;
    }

    public isSceneRecognition(): boolean {
        return this.type === DatasetType.SceneRecognition;
    }

    public isSegmentation(): boolean {
        return this.type === DatasetType.Segmentation;
    }

    // Used for static classifier brains
    public isFrozenBrainOutdated(): boolean {
        if (this.brain && this.brain.dateFrozen) {
            return this.brain.frozenDirty;
        }

        return false;
    }

    // Used for anomaly hi-fi brains
    public isHiFiBrainOutdated(): boolean {
        if (this.brain && this.brain.dateHiFiCreated) {
            return this.brain.hiFiDirty;
        }

        return false;
    }

    public isLearningBrainTrained(): boolean {
        return !!(this.brain && this.brain.dateTrained);
    }

    public hasAnomalyHiFiBrain(): boolean {
        return !!(this.brain && this.brain.dateHiFiCreated);
    }

    public getDefaultBrainVersionType(): BrainVersionType {
        return BrainVersionType.Learning;
    }

    public canExplain(): boolean {
        return this.isAnomalyRecognition();
    }

    public hasBrain(): boolean {
        return this.brain !== undefined;
    }

    public canHaveClassesWithWhitespace(): boolean {
        return !this.isObjectDetection();
    }

    public canTrainDetectionBrain(): boolean {
        if (this.userDefinedValidationSet) {
            return this.canTrainStaticDetectionBrain();
        } else {
            return this.canTrainDynamicDetectionBrain();
        }
    }

    /**
     * See BB-5839, In order to really know this before attempting to train we would need to go through all tagged
     * images and determine that each class has a bounding box present on at least one "train" image and one "validation" image.
     * Since the train/validation images aren't actually known pre-training in BrainBuilder at this time, we have to make a guess.
     */
    public canTrainDynamicDetectionBrain(): boolean {
        return this.imageCounts && this.imageCounts.tagged >= 5 && this.hasPolygons();
    }

    public canTrainStaticDetectionBrain(): boolean {
        if (!this.learningTrainFrames || !this.learningValidationFrames) {
            return false;
        }

        for (const classIdentifier in this.classPurposeCounts) {
            if (
                !this.classPurposeCounts[classIdentifier].train ||
                !this.classPurposeCounts[classIdentifier].validation
            ) {
                return false;
            }
        }

        return true;
    }

    // See BB-5839, Isolate the "train" condition for the checklist
    public doesStaticDetectorPassTrainChecklist(): boolean {
        if (this.learningTrainFrames == 0) {
            return false;
        }

        for (const classIdentifier in this.classPurposeCounts) {
            const train = this.classPurposeCounts[classIdentifier].train;
            if (train !== null && train == 0) {
                return false;
            }
        }

        return true;
    }

    // See BB-5839, Isolate the "validate" condition for the checklist
    public doesStaticDetectorPassValidationChecklist(): boolean {
        if (this.learningValidationFrames == 0) {
            return false;
        }

        for (const classIdentifier in this.classPurposeCounts) {
            const validation = this.classPurposeCounts[classIdentifier].validation;
            if (validation !== null && validation == 0) {
                return false;
            }
        }

        return true;
    }

    public hasRegionOfInterest(): boolean {
        return this.roi !== null;
    }
}

@Injectable()
export class DatasetProvider extends BaseProvider {
    public datasets: Dataset[] = [];
    public datasetIndexes: object = {};
    public stats: UserAssignmentStats;
    public currentDatasetIdentifier: string;

    constructor(
        public api: API,
        public classProvider: ClassProvider,
        public userProvider: UserProvider,
        private localStorageProvider: LocalStorageProvider,
        private boostProvider: BoostProvider,
        private events: Events,
        private translate: TranslateService
    ) {
        super();

        this.events.subscribe(EventName.AssetDeleted, data => {
            this.decrementDatasetImageCount(data.datasetIdentifier);
        });
    }

    public getCurrentDatasetIdentifier(): string {
        return this.currentDatasetIdentifier;
    }

    public setCurrentDatasetIdentifier(datasetIdentifier: string): void {
        this.currentDatasetIdentifier = datasetIdentifier;
    }

    private decrementDatasetImageCount(datasetIdentifier: string): void {
        const dataset = this.getDatasetById(datasetIdentifier);

        if (!dataset || !dataset.imageCounts || !dataset.imageCounts.total || dataset.imageCounts.total == 0) {
            return;
        }

        dataset.imageCounts.total -= 1;
    }

    public getDatasetNamePrefix(datasetName: string): string {
        return datasetName.toLowerCase().includes('dataset') ? '' : ` ${this.translate.instant('Dataset')}:`;
    }

    public getDatasetStats(): UserAssignmentStats {
        return this.stats;
    }

    public calculateDatasetStats(): void {
        let inProgress = 0;
        let completed = 0;
        let classes = 0;
        let tagged = 0;
        let untagged = 0;
        let numFrozenBrains = 0;
        let numBrainDownloads = 0;
        let images = 0;
        this.datasets.forEach(dataset => {
            tagged += dataset.imageCounts.tagged;
            classes += Object.keys(dataset.classCounts).length;
            completed += dataset.approved ? 1 : 0;
            images += dataset.imageCounts.total;
            numFrozenBrains += 0;
            numBrainDownloads += dataset.brain ? dataset.brain.numDownloads : 0;
        });

        untagged = images - tagged;
        inProgress = this.datasets.length - completed;

        this.stats = {
            inProgress,
            completed,
            classes,
            tagged,
            untagged,
            images,
            numFrozenBrains,
            numBrainDownloads
        };
    }

    public async fetchDatasets(): Promise<Dataset[]> {
        try {
            await this.ensureDependenciesLoaded([this.classProvider, this.userProvider]);

            const response = await this.api.get('datasets');

            this.datasetIndexes = {};
            this.datasets = await Promise.all<Dataset>(
                response.message.datasets.map(async (d, index) => {
                    this.datasetIndexes[d.datasetIdentifier] = index;
                    return this.toDataset(d);
                })
            );

            this.calculateDatasetStats();

            this.events.publish(EventName.DatasetProviderFetchDatasets);

            return this.datasets;
        } catch (err) {
            throw err;
        }
    }

    public async fetchDatasetsDebounce(): Promise<Dataset[]> {
        return this.debounce(
            'fetchDatasets',
            async () => {
                return await this.fetchDatasets();
            },
            250
        );
    }

    /**
     * Reload a dataset from BrainBank and replace the details of the local dataset object
     *
     * @param id The identifier of the dataset to reload
     * @param silent If true, don't show any warnings if the brain is out of sync
     */
    public async reloadDataset(id: string, silent: boolean = false): Promise<Dataset> {
        try {
            // Sometimes, several page components all need updated dataset info.
            // Debouncing helps reduce the load on the backend.
            const response = await this.debounce(
                id,
                () => {
                    return this.api.get(`datasets/${id}`);
                },
                250
            );

            const dataset = this.toDataset(response.message);
            const location = this.datasetIndexes[id];

            if (Number.isInteger(location)) {
                const unrefreshedDataset = this.datasets[location];

                // If we've managed to dirty up a trainable brain, show an "out of sync" warning.
                await this.handleTrainingStatus(dataset, unrefreshedDataset, silent);

                this.datasets[location] = dataset;
            } else {
                this.datasets.push(dataset);
                this.datasetIndexes[dataset.id] = this.datasets.length - 1;
            }

            this.events.publish(EventName.DatasetReloaded);

            return dataset;
        } catch (err) {
            throw err;
        }
    }

    private async handleTrainingStatus(dataset: Dataset, unrefreshedDataset: Dataset, silent: boolean) {
        if (
            !silent &&
            dataset.brain !== undefined &&
            !dataset.isObjectDetection() // Detector brains don't use the "retrain" job type
        ) {
            if (this.datasetIsNewlyDirty(unrefreshedDataset, dataset)) {
                await this.boostProvider.showRetrainProgress(dataset.id, BoostJobUXState.OUT_OF_SYNC, true);
            } else if (this.datasetIsNewlyInNeedOfIncrementalTraining(unrefreshedDataset, dataset)) {
                await this.boostProvider.showRetrainProgress(
                    dataset.id,
                    BoostJobUXState.NEEDS_INCREMENTAL_TRAINING,
                    true
                );
            }
        }
    }

    /**
     * Return true if the specified dataset now appears to have a brain in the dirty state
     * as compared to the state of the dataset before it was reloaded
     *
     * @param unrefreshedDataset The state of the dataset before being reloaded
     * @param dataset The current state of the dataset
     */
    private datasetIsNewlyDirty(unrefreshedDataset: Dataset, dataset: Dataset): boolean {
        return (
            !this.isDatasetBrainDirty(unrefreshedDataset) && this.isDatasetBrainDirty(dataset) // Unrefreshed brain is clean
        ); // Refreshed brain is dirty)
    }

    /**
     * Return true if the specified dataset now appears to have newly tagged assets that
     * require incremental training as compared to the state of the dataset before it was reloaded
     *
     * @param unrefreshedDataset The state of the dataset before being reloaded
     * @param dataset The current state of the dataset
     */
    private datasetIsNewlyInNeedOfIncrementalTraining(unrefreshedDataset: Dataset, dataset: Dataset): boolean {
        return (
            !this.hasNewlyTaggedAssetsForRecognitionTraining(unrefreshedDataset) &&
            this.hasNewlyTaggedAssetsForRecognitionTraining(dataset)
        );
    }

    public getBrainIdentifierFromDataset(dataset: Dataset): string | undefined {
        if (!dataset || !dataset.brain || !dataset.brain.id) {
            return;
        }

        return dataset.brain.id;
    }

    public isDatasetBrainDirty(dataset: Dataset | undefined): boolean {
        return dataset !== undefined && dataset.brain !== undefined && dataset.brain.dirty;
    }

    public async load(force: boolean = false): Promise<Dataset[]> {
        return await this.ensureLoaded(() => this.fetchDatasetsDebounce(), force);
    }

    private toDataset(obj: any): Dataset {
        const dataset = new Dataset();
        dataset.id = obj.datasetIdentifier;
        dataset.name = obj.name;

        switch (obj.usageType) {
            case 'Scene Recognition':
                dataset.type = DatasetType.SceneRecognition;
                break;
            case 'Anomaly Recognition':
                dataset.type = DatasetType.AnomalyRecognition;
                break;
            case 'Detection':
                dataset.type = DatasetType.ObjectDetection;
                break;
            case 'Segmentation':
                dataset.type = DatasetType.Segmentation;
                break;
            default:
                break;
        }

        dataset.imageCounts = obj.imageCounts;
        dataset.classCounts = obj.classCounts;
        dataset.classPurposeCounts = obj.classPurposeCounts;
        dataset.previewImageUrls = obj.previewImageUrls || [];
        dataset.projectId = obj.projectIdentifier;
        dataset.users = obj.users || [];
        dataset.approved = obj.datasetApproved;
        dataset.truePositivePreference = obj.truePositivePreference;
        dataset.commentCounts = obj.commentCounts || new DatasetComments();
        dataset.numPolygons = 0;
        dataset.adherenceToNormal = obj.adherenceToNormal;
        dataset.hifiAdherenceToNormal = obj.hifiAdherenceToNormal;
        dataset.perUserTaggedCounts = obj.userTaggedCounts;
        Object.keys(dataset.classCounts).forEach(classIdentifier => {
            dataset.numPolygons += dataset.classCounts[classIdentifier];
        });
        dataset.roi = obj.roi;
        dataset.enableStaticClassifier = obj.enableStaticClassifier;
        dataset.enableAnomalyHiFi = obj.enableAnomalyHiFi;
        dataset.userDefinedValidationSet = obj.userDefinedValidationSet;
        dataset.learnAsYouGo = obj.learnAsYouGo;
        dataset.learningTrainFrames = obj.learningTrainFrames;
        dataset.learningValidationFrames = obj.learningValidationFrames;

        if (obj.brain) {
            dataset.brain = {
                id: obj.brain.brainIdentifier,
                dateTrained: obj.brain.dateTrained,
                dateFrozen: obj.brain.dateFrozen ? new Date(obj.brain.dateFrozen) : null,
                dateHiFiCreated: obj.brain.dateHiFiCreated ? new Date(obj.brain.dateHiFiCreated) : null,
                accuracy: obj.brain.accuracy,
                numValidationPolygons: obj.brain.numValidationPolygons,
                numTrainedPolygons: obj.brain.numTrainedPolygons,
                numFrames: obj.brain.numFrames,
                dirty: obj.brain.dirty,
                hiFiDirty: obj.brain.hiFiDirty,
                frozenDirty: obj.brain.frozenDirty,
                promoted: obj.brain.promoted,
                classCounts: obj.brain.classCounts,
                numDownloads: obj.brain.numDownloads,
                averagePrecision: obj.brain.averagePrecision,
                averageRecall: obj.brain.averageRecall,
                miapCOCO: obj.brain.miapCOCO,
                miapVOC: obj.brain.miapVOC
            };
        }

        return dataset;
    }

    public getDatasetById(id: string): Dataset | undefined {
        const index = this.datasetIndexes[id];
        return this.datasets[index];
    }

    /**
     * Create a new dataset
     */
    public async createDataset(
        name: string,
        usageType: DatasetType,
        projectIdentifier?: string,
        adherenceToNormal?: AdherenceToNormal,
        truePositivePreference?: TruePositiveDefinition,
        enableStaticClassifier: boolean | null = null,
        enableAnomalyHiFi: boolean | null = null
    ): Promise<Dataset> {
        try {
            const createDatasetOptions = {
                name,
                usageType,
                projectIdentifier
            };

            if (usageType == DatasetType.AnomalyRecognition) {
                if (adherenceToNormal) {
                    createDatasetOptions['adherenceToNormal'] = adherenceToNormal;
                }

                if (truePositivePreference) {
                    createDatasetOptions['truePositivePreference'] = truePositivePreference;
                }

                if (enableAnomalyHiFi) {
                    createDatasetOptions['enableAnomalyHiFi'] = enableAnomalyHiFi;
                }
            }

            if (usageType == DatasetType.SceneRecognition) {
                createDatasetOptions['enableStaticClassifier'] = enableStaticClassifier;
            } else {
                createDatasetOptions['enableStaticClassifier'] = null;
            }

            const response = await this.api.post('datasets', createDatasetOptions);

            const dataset = this.toDataset({
                datasetIdentifier: response.message,
                name,
                imageCounts: { tagged: 0, total: 0 },
                classCounts: {},
                projectId: projectIdentifier,
                users: []
            });

            this.datasets = [dataset, ...this.datasets].sort((a, b) => a.name.localeCompare(b.name));
            this.datasets.forEach((d, index) => {
                this.datasetIndexes[d.id] = index;
            });

            await this.reloadDataset(dataset.id);

            return dataset;
        } catch (err) {
            throw err;
        }
    }

    /** This method generates pie and bar chart data for visualizing the classes in a dataset
     * @param {string} id The identifier for the dataset we want to visualize.
     */
    public getClassesChartData(id: string): ClassesChartData[] {
        const dataset = this.getDatasetById(id);

        if (!dataset) {
            return [];
        }

        const datasetClasses = this.classProvider.getDatasetClasses(dataset.id);
        return datasetClasses.map(classObject => {
            let classCountsForClassObject;

            if (dataset.isObjectDetection() || dataset.isSegmentation()) {
                const trainingValue = dataset.classCounts[classObject.id];

                return {
                    trainingValue: trainingValue ? trainingValue : 0,
                    validationValue: 0,
                    name: classObject.name,
                    color: classObject.color
                };
            } else if (dataset.brain) {
                classCountsForClassObject = dataset.brain.classCounts[classObject.id];
            }

            return {
                trainingValue: classCountsForClassObject ? classCountsForClassObject.train : 0,
                validationValue: classCountsForClassObject ? classCountsForClassObject.validation : 0,
                name: classObject.name,
                color: classObject.color
            };
        });
    }

    public async renameDataset(newName: string, datasetIdentifier: string) {
        try {
            await this.api.post(`datasets/${datasetIdentifier}/rename`, {
                name: newName
            });
        } catch (e) {
            throw e;
        }
    }

    /**
     * Returns true if given dataset has brain deployment capabilities.
     *
     * @param datasetIdentifier the dataset to check
     */
    public hasBrainCapability(datasetIdentifier: string): boolean {
        const dataset = this.getDatasetById(datasetIdentifier);
        if (!dataset || !dataset.type) {
            return false;
        }
        return (
            dataset.type == DatasetType.SceneRecognition ||
            (dataset.type == DatasetType.AnomalyRecognition &&
                this.userProvider.hasEnableAnomalyRecognitionCapability()) ||
            (dataset.type == DatasetType.ObjectDetection && this.userProvider.hasDetectionBrainsCapability())
        );
    }

    public getDatasets(limit: number = this.datasets.length): Dataset[] {
        return this.datasets.slice(0, limit);
    }

    public getDatasetsForProject(projectID: string): Dataset[] {
        return this.datasets.filter(dataset => dataset.projectId === projectID);
    }

    public getDatasetProjectID(dataset: Dataset): string | undefined {
        // NOTE: We used to block users without project management privileges from running this function.
        // However, this causes problems with the nav sidebar breadcrumbs for ordinary organization users,
        // so this check has been removed.

        return dataset && dataset.projectId;
    }

    public async assignUsers(datasetIdentifier: string, userIdentifiers: string[]): Promise<boolean> {
        try {
            const response = await this.api.post(`datasets/${datasetIdentifier}/users`, {
                userIdentifiers
            });
            return response.success;
        } catch (error) {
            throw error;
        }
    }

    public async deleteUser(datasetIdentifier: string, userIdentifier: string): Promise<boolean> {
        try {
            const response = await this.api.delete(`datasets/${datasetIdentifier}/users/${userIdentifier}`);
            return response.success;
        } catch (error) {
            throw error;
        }
    }

    public async approveDataset(datasetIdentifier: string): Promise<boolean> {
        try {
            const response = await this.api.post(`datasets/${datasetIdentifier}/approve`);
            await this.reloadDataset(datasetIdentifier);
            return response.success;
        } catch (error) {
            throw error;
        }
    }

    public async unapproveDataset(datasetIdentifier: string): Promise<boolean> {
        try {
            const response = await this.api.post(`datasets/${datasetIdentifier}/unapprove`);
            await this.reloadDataset(datasetIdentifier);
            return response.success;
        } catch (error) {
            throw error;
        }
    }

    public async train(datasetIdentifier: string): Promise<void> {
        const incrementalTrain = await this.api.post(`datasets/${datasetIdentifier}/train?wait=1`);

        if (incrementalTrain && incrementalTrain.success) {
            this.events.publish(EventName.TrainFinished);
        }
    }

    public async deactivateDataset(id: string): Promise<void> {
        try {
            await this.api.delete(`datasets/${id}`);

            this.events.publish(EventName.DatasetProviderDelete, this.getDatasetById(id));

            await this.load(true);
        } catch (err) {
            throw err;
        }
    }

    public async setTruePositivePreference(
        datasetIdentifier: string,
        truePositiveDefinition: TruePositiveDefinition
    ): Promise<void> {
        try {
            await this.api.put(`datasets/${datasetIdentifier}`, { truePositivePreference: truePositiveDefinition });
        } catch (err) {
            throw err;
        }
    }

    public async setEnableStaticClassifier(datasetIdentifier: string, enableStaticClassifier: Boolean): Promise<void> {
        try {
            await this.api.put(`datasets/${datasetIdentifier}`, { enableStaticClassifier });
            this.events.publish(EventName.DatasetSetEnableStaticClassifier, datasetIdentifier);
        } catch (err) {
            throw err;
        }
    }

    public async setEnableAnomalyHiFi(datasetIdentifier: string, enableAnomalyHiFi: Boolean): Promise<void> {
        try {
            await this.api.put(`datasets/${datasetIdentifier}`, { enableAnomalyHiFi });
            this.events.publish(EventName.DatasetSetEnableAnomalyHiFi, datasetIdentifier);
        } catch (err) {
            throw err;
        }
    }

    public async setLearnAsYouGo(datasetIdentifier: string, learnAsYouGo: Boolean): Promise<void> {
        try {
            await this.api.put(`datasets/${datasetIdentifier}`, { learnAsYouGo });
            this.events.publish(EventName.DatasetSetLearnAsYouGo, datasetIdentifier);
        } catch (err) {
            throw err;
        }
    }

    public async setActiveAssetForDataset(datasetIdentifier: string, assetIdentifier: string): Promise<void> {
        try {
            if (!datasetIdentifier || !assetIdentifier) {
                return;
            }

            await this.localStorageProvider.set(`dataset.${datasetIdentifier.slice(0, 8)}.asset`, assetIdentifier);
        } catch (err) {
            throw err;
        }
    }

    public async clearActiveAssetForDataset(datasetIdentifier: string): Promise<void> {
        try {
            if (!datasetIdentifier) {
                return;
            }

            await this.localStorageProvider.remove(`dataset.${datasetIdentifier.slice(0, 8)}.asset`);
        } catch (err) {
            throw err;
        }
    }

    public async getActiveAssetForDataset(datasetIdentifier: string): Promise<string | undefined> {
        try {
            if (!datasetIdentifier) {
                return;
            }

            return await this.localStorageProvider.get(`dataset.${datasetIdentifier.slice(0, 8)}.asset`);
        } catch (err) {
            throw err;
        }
    }

    /**
     * Removes dataset information for the user
     */
    public resetInfo(): void {
        this.datasets = [];
        this.datasetIndexes = {};
        super.resetInfo();
    }

    public getRenderableDatasetTypeFromDatasetId(id: string): string {
        const dataset = this.getDatasetById(id);

        if (!dataset) {
            return '';
        }

        return !dataset
            ? ''
            : this.getRenderableDatasetType(dataset.type, dataset.enableStaticClassifier, dataset.enableAnomalyHiFi);
    }

    public getRenderableDatasetType(
        datasetType: DatasetType,
        enableStaticClassifier = false,
        enableAnomalyHiFi = false
    ): string {
        switch (datasetType) {
            case DatasetType.ObjectDetection:
                return this.userProvider.hasDetectionBrainsCapability() ? 'Detector' : 'Detection';
            case DatasetType.SceneRecognition:
                return enableStaticClassifier ? 'Classifier' : 'Learning Classifier';
            case DatasetType.AnomalyRecognition:
                return enableAnomalyHiFi ? 'Anomaly Recognizers' : 'Anomaly Recognizer';
            case DatasetType.Segmentation:
                return 'Segmentation';
            default:
                return '';
        }
    }

    /**
     * Returns the IDs of the "normal" and "anomaly" classes in an object.
     * Should only be called for anomaly recognizer datasets.
     */
    public findFixedAnomalyClasses(
        datasetIdentifier: string
    ): { anomalyClass: Class | undefined; normalClass: Class | undefined } {
        const classes = this.classProvider.getDatasetClasses(datasetIdentifier);
        return {
            anomalyClass: classes.find(c => c.name === 'Anomaly'),
            normalClass: classes.find(c => c.name === 'Normal')
        };
    }

    public showUploadExamplesOfAnomaly(dataset: Dataset): boolean {
        if (!dataset || dataset.type !== DatasetType.AnomalyRecognition) {
            return false;
        }

        if (!this.userProvider.canManageDatasets()) {
            return false;
        }

        const classes = this.findFixedAnomalyClasses(dataset.id);
        const anomalyClass = classes ? classes.anomalyClass : null;
        const normalClass = classes ? classes.normalClass : null;

        if (!anomalyClass || !normalClass) {
            return false;
        }

        // We must assign to 0 if undefined, otherwise undefined <= 0 will return false
        const anomalyAssetsUploaded = dataset.classCounts[anomalyClass.id] || 0;
        const normalAssetsUploaded = dataset.classCounts[normalClass.id] || 0;
        return anomalyAssetsUploaded <= 0 && normalAssetsUploaded > 0;
    }

    public canEvaluateBrain(datasetIdentifier: string): boolean {
        const dataset = this.getDatasetById(datasetIdentifier);

        if (!dataset) {
            return false;
        }

        return (
            dataset !== undefined &&
            dataset.brain !== undefined &&
            dataset.brain.accuracy != null &&
            dataset.brain.numTrainedPolygons > 0 &&
            dataset.imageCounts.tagged > 0
        );
    }

    public canTrainDetectionBrain(datasetIdentifier: string): boolean {
        // In order to train a detection brain, the user's dataset needs to contain at least
        // 5 tagged images.
        const dataset = this.getDatasetById(datasetIdentifier);

        if (!dataset || dataset.type != DatasetType.ObjectDetection) {
            return false;
        }

        return dataset.canTrainDetectionBrain();
    }

    public showUploadExamplesOfNormal(dataset: Dataset): boolean {
        if (!dataset || dataset.type !== DatasetType.AnomalyRecognition) {
            return false;
        }

        if (!this.userProvider.canManageDatasets()) {
            return false;
        }

        const classes = this.findFixedAnomalyClasses(dataset.id);
        const normalClass = classes ? classes.normalClass : null;

        if (!normalClass) {
            return false;
        }

        // We must assign to 0 if undefined, otherwise undefined <= 0 will return false
        const normalAssetsUploaded = dataset.classCounts[normalClass.id] || 0;
        return normalAssetsUploaded < 20 && dataset.imageCounts.total > 0;
    }

    public canOptimizeDataset(datasetIdentifier: string): boolean {
        const dataset = this.getDatasetById(datasetIdentifier);
        if (!dataset) {
            return false;
        }

        return dataset.canOptimizeDataset();
    }

    public generateDefaultDuplicatedDatasetName(originalDataset: Dataset): string {
        // the below RegEx parentheses extraction expression cana be credited to: https://stackoverflow.com/a/17779833
        const parenthesesSearchExpression = /\(([^)]+)\)/;

        const parseName = (datasetName: string): { baseName: string; parensNumber: number } => {
            let baseName = datasetName;
            let parensNumber = 0;

            if (parenthesesSearchExpression.test(datasetName)) {
                const extractedNumberWithParens = parenthesesSearchExpression.exec(datasetName);
                if (extractedNumberWithParens) {
                    const extractedNumber = parseInt(extractedNumberWithParens[1], 10);
                    const spaceI = extractedNumberWithParens.index - 1;

                    if (extractedNumber && datasetName[spaceI] == ' ') {
                        parensNumber = extractedNumber;
                        baseName = datasetName.substr(0, extractedNumberWithParens.index - 1);
                    }
                }
            }

            return { baseName, parensNumber };
        };

        const projectDatasets = this.datasets.filter(
            dataset => dataset.projectId == originalDataset.projectId && dataset.id != originalDataset.id
        );

        const parsedOriginalDataset = parseName(originalDataset.name);
        let numOfDuplicates = parsedOriginalDataset.parensNumber + 1;

        for (const dataset of projectDatasets) {
            const parsedName = parseName(dataset.name);

            if (parsedName.baseName == parsedOriginalDataset.baseName && parsedName.parensNumber >= numOfDuplicates) {
                numOfDuplicates = parsedName.parensNumber + 1;
            }
        }

        return `${parsedOriginalDataset.baseName} (${numOfDuplicates})`;
    }

    /**
     * Start dataset duplication process
     *
     * @param {DatasetDuplicationOptions} duplicationOptions
     * @description Kickoff the dataset duplication job and return the job ID
     * @returns {Promise<string>} jobID
     * @memberof DatasetProvider
     */
    public async kickoffDatasetDuplication(duplicationOptions: DatasetDuplicationOptions): Promise<string> {
        const options: any = {
            duplicatedDatasetUsageType: duplicationOptions.duplicatedDatasetUsageType,
            duplicatedDatasetName: duplicationOptions.duplicatedDatasetName,
            includeUntaggedAssets: duplicationOptions.includeUntaggedAssets
        };

        if (
            duplicationOptions.classIdentifiers &&
            Array.isArray(duplicationOptions.classIdentifiers) &&
            duplicationOptions.classIdentifiers.length > 0
        ) {
            options['classIdentifiers'] = duplicationOptions.classIdentifiers;
        }

        const duplicateResponse = await this.api.post(`datasets/${duplicationOptions.dataset.id}/duplicate`, options);
        return duplicateResponse.message.jobIdentifier;
    }

    public async cancelDuplication(datasetIdentifier: string, jobIdentifier: string): Promise<void> {
        await this.api.post(`datasets/${datasetIdentifier}/duplicate/${jobIdentifier}/cancel`);
    }

    public canDatasetUseExplainabilityService(dataset: Dataset): boolean {
        return dataset && dataset.canExplain();
    }

    public async runDatasetChecks(datasetIdentifier: string, checks: DatasetChecks[] = []): Promise<any> {
        let url = `datasets/${datasetIdentifier}/checks`;

        if (checks.length) {
            url += `?check=${checks.join('&check=')}`;
        }

        const response = await this.api.get(url);

        return response.message;
    }

    public canDeployBrain(dataset: Dataset): boolean {
        return this.canEvaluateBrain(dataset.id) && this.canTestBrain(dataset);
    }

    public canTestBrain(dataset: Dataset): boolean {
        return this.isLearningBrainTrained(dataset);
    }

    public isFrozenBrainTrained(dataset: Dataset): boolean {
        return dataset.brain !== undefined && dataset.brain.dateFrozen !== null;
    }

    public isHiFiBrainTrained(dataset: Dataset): boolean {
        return dataset.brain !== undefined && dataset.brain.dateHiFiCreated !== null;
    }

    public isLearningBrainTrained(dataset: Dataset): boolean {
        return dataset.brain !== undefined && dataset.brain.dateTrained !== null;
    }

    public getBrainTrainedDateKey(brainVersionType: BrainVersionType): string {
        switch (brainVersionType) {
            case BrainVersionType.Learning:
            case BrainVersionType.Custom:
                return 'dateTrained';

            case BrainVersionType.AnomalyHiFi:
                return 'dateHiFiCreated';

            case BrainVersionType.Frozen:
                return 'dateFrozen';
        }
    }

    public hasNewlyTaggedAssetsForRecognitionTraining(dataset: Dataset | null | undefined) {
        if (
            dataset &&
            (dataset.type == DatasetType.AnomalyRecognition || dataset.type == DatasetType.SceneRecognition) &&
            dataset.brain &&
            dataset.imageCounts
        ) {
            return dataset.brain.numFrames < dataset.imageCounts.tagged;
        }

        return false;
    }

    public getLearnAsYouGo(dataset: Dataset | null | undefined) {
        return dataset && dataset.learnAsYouGo ? true : false;
    }
}



// WEBPACK FOOTER //
// ./src/providers/dataset.ts